import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Bell, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Notice {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  priority: number;
}

export default function HHTNoticeBoardScreen() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 0,
    is_active: true
  });

  useEffect(() => {
    checkAdminStatus();
    fetchNotices();
  }, []);

  const checkAdminStatus = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAdmin(!!session);
  };

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      let query = supabase
        .from('notices')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (!session) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching notices:', error);
      } else {
        setNotices(data || []);
      }
    } catch (err) {
      console.error('Exception while fetching notices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingNotice) {
      const { error } = await supabase
        .from('notices')
        .update({
          title: formData.title,
          content: formData.content,
          priority: formData.priority,
          is_active: formData.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingNotice.id);

      if (error) {
        console.error('Error updating notice:', error);
        alert('Failed to update notice');
      } else {
        setEditingNotice(null);
        resetForm();
        fetchNotices();
      }
    } else {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('notices')
        .insert([{
          title: formData.title,
          content: formData.content,
          priority: formData.priority,
          is_active: formData.is_active,
          created_by: user?.id
        }]);

      if (error) {
        console.error('Error creating notice:', error);
        alert('Failed to create notice');
      } else {
        setShowAddForm(false);
        resetForm();
        fetchNotices();
      }
    }
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      priority: notice.priority,
      is_active: notice.is_active
    });
    setShowAddForm(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this notice?')) return;

    const { error } = await supabase
      .from('notices')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting notice:', error);
      alert('Failed to delete notice');
    } else {
      fetchNotices();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 0,
      is_active: true
    });
  };

  const cancelEdit = () => {
    setEditingNotice(null);
    setShowAddForm(false);
    resetForm();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <nav className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Harvest House Tobique
              </span>
            </Link>
            {isAdmin && (
              <Link
                to="/admin-dashboard"
                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all"
              >
                Admin Dashboard
              </Link>
            )}
          </div>
        </div>
      </nav>

      <div className="relative h-80 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/image.png"
            alt="HHT Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-teal-900/50 to-cyan-900/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Bell className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">HHT Notice Board</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto drop-shadow">
              Your hub for community updates, announcements, and important information
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isAdmin && (
          <div className="mb-8">
            {!showAddForm && !editingNotice && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-700 transform hover:scale-105 transition-all shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                Post New Notice
              </button>
            )}

            {(showAddForm || editingNotice) && (
              <div className="bg-white rounded-xl shadow-xl p-8 border-2 border-teal-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingNotice ? 'Edit Notice' : 'Post New Notice'}
                  </h2>
                  <button
                    onClick={cancelEdit}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring focus:ring-teal-200 transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Content
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring focus:ring-teal-200 transition-all resize-none"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Priority
                      </label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:ring focus:ring-teal-200 transition-all"
                      />
                      <p className="text-xs text-gray-500 mt-1">Higher numbers appear first</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Status
                      </label>
                      <div className="flex items-center h-12">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="ml-3 text-gray-700">Active</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4 pt-4">
                    <button
                      type="submit"
                      className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-700 transform hover:scale-105 transition-all shadow-lg"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      {editingNotice ? 'Update Notice' : 'Post Notice'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading notices...</p>
          </div>
        ) : notices.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border-2 border-gray-100">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No notices posted yet</h3>
            <p className="text-gray-500">
              {isAdmin ? 'Click "Post New Notice" to share your first update.' : 'Check back soon for community updates.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {notices.map((notice) => (
              <div
                key={notice.id}
                className={`bg-white rounded-xl shadow-lg overflow-hidden border-l-4 ${
                  notice.is_active ? 'border-teal-500' : 'border-gray-300'
                } transition-all hover:shadow-xl`}
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-2xl font-bold text-gray-800">{notice.title}</h3>
                        {!notice.is_active && (
                          <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                            Inactive
                          </span>
                        )}
                        {notice.priority > 0 && (
                          <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                            Priority: {notice.priority}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Posted on {formatDate(notice.created_at)}
                        {notice.updated_at !== notice.created_at && (
                          <span> • Updated {formatDate(notice.updated_at)}</span>
                        )}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(notice)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit notice"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(notice.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete notice"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {notice.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
